import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Sparkles, HeartHandshake, ArrowRight } from 'lucide-react';

const POINTS = [
  { icon: ShieldCheck, title: 'Halal Certified', desc: '100% halal food you can trust.' },
  { icon: Sparkles, title: 'Hygienic & Safe', desc: 'Prepared fresh in clean and hygienic conditions.' },
  { icon: HeartHandshake, title: 'Made With Love', desc: 'Every recipe is made with passion and care.' },
];

// The v3 snapshot pointed this at a generated-images URL that is actually a
// handbag product shot from an unrelated storefront. Using the project's own
// uploaded food photograph instead, the only real imagery this business has.
const CATERING_IMAGE =
  'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/88239fe1-d299-408c-8199-9a47a84d90e7/1786981851786-xp0wdi.png';

export default function WhyChooseUs() {
  return (
    <section id="about" className="relative grid grid-cols-1 lg:grid-cols-2">
      {/* ---------- Left: black "why choose us" panel ---------- */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="relative overflow-hidden px-5 md:px-8 py-12 md:py-14 lg:py-16 lg:pl-[max(2rem,calc((100vw-1280px)/2+2rem))] lg:pr-20"
        style={{ background: 'var(--color-ink)' }}
      >
        {/* faint dotted ground, echoing the mockup's map watermark */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '20px 20px',
          }}
          aria-hidden="true"
        />

        <div className="relative max-w-xl">
          <span
            className="text-xs font-extrabold uppercase tracking-[0.25em]"
            style={{ color: 'var(--color-yellow)' }}
          >
            Why Choose Us?
          </span>
          <h2 className="mt-4 display text-4xl md:text-5xl text-white rule-gold">
            Good Food
            <br />
            <span style={{ color: 'var(--color-yellow)' }}>Good Mood</span>
          </h2>
          <p className="mt-6 text-sm md:text-[15px] text-white/70 leading-relaxed max-w-md">
            We&apos;re passionate about serving high-quality, halal certified food that brings people
            together. Every dish is made with care, hygiene and authentic flavors.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-7">
            {POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <span
                  className="inline-flex w-11 h-11 rounded-full border-2 items-center justify-center"
                  style={{ borderColor: 'rgba(255,255,255,0.25)' }}
                >
                  <p.icon size={18} className="text-white" />
                </span>
                <h4
                  className="mt-3 text-[13px] font-extrabold uppercase tracking-wide"
                  style={{ color: 'var(--color-yellow)' }}
                >
                  {p.title}
                </h4>
                <p className="mt-1.5 text-[13px] text-white/60 leading-snug">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ---------- Right: catering panel over the platter shot ---------- */}
      <motion.div
        id="catering"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="relative min-h-[420px] flex items-center"
      >
        <img
          src={CATERING_IMAGE}
          alt="Catering trays of halal rice and grilled meats"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Cream wash: near-solid on the text side, clearing to show the food on the right. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, var(--color-cream) 0%, var(--color-cream) 42%, rgba(248,244,233,0.85) 58%, rgba(248,244,233,0) 88%)',
          }}
        />

        <div className="relative px-5 md:px-8 py-12 md:py-14 lg:py-16 lg:pr-[max(2rem,calc((100vw-1280px)/2+2rem))] max-w-lg">
          <span
            className="text-xs font-extrabold uppercase tracking-[0.25em]"
            style={{ color: 'var(--color-ink)' }}
          >
            Catering
          </span>
          <h3 className="mt-3 display text-3xl md:text-4xl leading-none rule-gold">
            <span style={{ color: 'var(--color-ink)' }}>Catering For</span>
            <br />
            <span className="text-ink">Every Occasion</span>
          </h3>
          <p className="mt-6 text-sm md:text-[15px] text-ink-2 leading-relaxed max-w-sm">
            From small gatherings to large events, we&apos;ve got you covered with our delicious halal
            menu, family feasts and combo platters, fresh to your table.
          </p>
          <Link to="/contact" className="btn-green mt-7">
            Enquire Now <ArrowRight size={15} />
          </Link>
        </div>
      </motion.div>
    </section>
  );
}
